import { createStore } from 'vuex'
import * as mutationTypes from './mutation-types'
import { getJSONValue, setJSONValue } from '@app/utils/local-storage'
import { SfLog } from '@app/adapters/logger'
import I18n from 'i18n-js'

// update every hour
const UPDATE_INTERVAL_MS = 1000 * 60 * 60

const getBeamerPosts = async (url) => {
  return fetch(url, {
    headers: { Accept: 'application/json' },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(
        `Changelog posts request failed with status ${response.status}`
      )
    }

    return response.json()
  })
}

export const mutations = {
  [mutationTypes.MARK_AS_READ](state) {
    state.hasNewChangelog = false
    setJSONValue('hasNewChangelog', false)
  },
  [mutationTypes.SET_NEW_CHANGELOG](state, newChangelog) {
    state.hasNewChangelog = true
    state.latestChangelog = newChangelog.title
    state.lastUpdated = new Date().getTime()
    state.changelogCategory = newChangelog.category
    state.changelogLocale = newChangelog.locale
    state.changelogSegment = newChangelog.segment
    setJSONValue('hasNewChangelog', true)
    setJSONValue('latestChangelog', newChangelog.title)
    setJSONValue('changelogLastUpdated', state.lastUpdated)
    setJSONValue('changelogCategory', newChangelog.category)
    setJSONValue('changelogLocale', newChangelog.locale)
    setJSONValue('changelogSegment', newChangelog.segment)
  },
  [mutationTypes.UPDATE_PERFORMED](state) {
    state.lastUpdated = new Date().getTime()
    setJSONValue('changelogLastUpdated', state.lastUpdated)
  },
}

const actions = {
  markAsRead({ commit }) {
    commit(mutationTypes.MARK_AS_READ)
  },

  updateChangelog({ commit, getters }) {
    if (!getters.updateNeeded) return

    getBeamerPosts(getters.postsUrl)
      .then((collection) => {
        if (!collection?.length) return

        const lastPost = collection[0]

        let postTranslation = lastPost.translations.find((translation) => {
          return translation.language === getters.locale.toUpperCase()
        })

        if (!postTranslation) {
          postTranslation = lastPost.translations.find((translation) => {
            return translation.language === I18n.defaultLocale.toUpperCase()
          })
        }

        if (!postTranslation) return

        commit(mutationTypes.SET_NEW_CHANGELOG, {
          title: postTranslation.title,
          category: postTranslation.category,
          locale: getters.locale,
          segment: getters.segment,
        })

        commit(mutationTypes.UPDATE_PERFORMED)
      })
      .catch((error) => {
        SfLog.warn('Failed to fetch changelog posts', { extra: { error } })
      })
  },
}

export const getters = {
  pulseUser: () => {
    Silverfin.isCurrentUserPulse()
  },

  updateNeeded: (state, getters) => {
    if (!state.lastUpdated) {
      return true
    }

    if (getters.locale !== state.changelogLocale) {
      return true
    }

    if (getters.segment !== state.changelogSegment) {
      return true
    }

    if (!state.latestChangelog) {
      return true
    }

    if (!state.changelogCategory) {
      return true
    }

    if (state.lastUpdated < new Date().getTime() - UPDATE_INTERVAL_MS) {
      return true
    }

    return false
  },
}

export default function BeamerBridge({
  postsUrl,
  segment,
  locale,
  changelogUrl,
}) {
  return createStore({
    state: {
      lastUpdated: getJSONValue('changelogLastUpdated'),
      hasNewChangelog: getJSONValue('hasNewChangelog') || false,
      latestChangelog: getJSONValue('latestChangelog') || '',
      changelogCategory: getJSONValue('changelogCategory') || '',
      changelogLocale: getJSONValue('changelogLocale') || '',
      changelogSegment: getJSONValue('changelogSegment') || '',
    },
    getters: {
      ...getters,
      postsUrl: () => postsUrl,
      segment: () => segment,
      locale: () => locale,
      changelogUrl: () => changelogUrl,
    },
    mutations,
    actions,
  })
}
